<template>
  <div>
    <b-sidebar
      id="sidebar-backdrop"
      backdrop
      shadow
      bg-variant="white"
      sidebar-class="sidebar-lg"
      no-header
      right
      :visible="userAddSidebarActive"
      @change="(val) => $emit('update:user-add-sidebar-active', val)"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            Nuevo Usuario
          </h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>
        <!-- Campos formulario -->
        <validation-observer ref="simpleRules">
          <b-form
            v-if="show"
            class="p-2"
            @submit="onSubmit"
            @reset="onReset"
          >
            <!-- Nombre -->
            <validation-provider
              v-slot="{ errors }"
              name="nombre"
              rules="required"
            >
              <b-form-group
                label="Nombre:"
                label-for="input-name"
              >
                <b-form-input
                  id="input-name"
                  v-model="form.name"
                  autocomplete="new-password"
                  placeholder="Ingresa el nombre"
                  required
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Correo -->
            <validation-provider
              v-slot="{ errors }"
              name="correo"
              rules="required|email"
            >
              <b-form-group
                label="Correo:"
                label-for="input-email"
              >
                <b-form-input
                  id="input-email"
                  v-model="form.mail"
                  autocomplete="new-password"
                  placeholder="Ingresa el correo"
                  required
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- ID de empleado -->
            <b-form-group
              label="ID de empleado:"
              label-for="input-employee-id"
            >
              <b-form-input
                id="input-employee-id"
                v-model="form.employee_id"
                placeholder="Ingresa el ID de empleado"
                :state="employeeIdState"
                @blur="validateEmployeeId"
              />
              <small
                v-if="employeeIdError"
                class="text-danger"
              >{{ employeeIdError }}</small>
            </b-form-group>

            <!-- Rol -->
            <validation-provider
              v-slot="{ errors }"
              name="rol"
              rules="required"
            >
              <b-form-group
                label="Rol:"
                label-for="select-rol"
              >
                <v-select
                  v-model="form.role"
                  autocomplete="new-password"
                  :options="roles"
                  :reduce="role => role.value"
                  :clearable="true"
                  placeholder="Selecciona un rol"
                  input-id="select-role"
                  required
                >
                  <template slot="no-options">
                    Lo siento, no se encontraron roles
                  </template>
                </v-select>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Meta de Venta -->
            <validation-provider
              v-slot="{ errors }"
              name="meta de venta"
              rules="decimal:2"
            >
              <b-form-group
                label="Meta de Venta (Q):"
                label-for="input-sales-target"
              >
                <b-form-input
                  id="input-sales-target"
                  v-model="form.sales_target"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ingresa la meta de venta"
                />
                <small class="text-danger">{{ errors[0] }}</small>
                <small class="text-muted">Dejar vacío si no aplica</small>
              </b-form-group>
            </validation-provider>

            <!-- Contraseña -->
            <validation-provider
              v-slot="{ errors }"
              name="contraseña"
              rules="required|password"
            >
              <b-form-group>
                <label for="input-pass">Contraseña</label>
                <b-input-group class="input-group-merge">
                  <b-form-input
                    id="input-pass"
                    v-model="form.password"
                    autocomplete="new-password"
                    :type="passwordType"
                    placeholder="Ingresa la contraseña"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePassword"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Validar Contraseña -->
            <validation-provider
              v-slot="{ errors }"
              name="confirmar contraseña"
              rules="required|confirmed:contraseña"
            >
              <b-form-group>
                <label for="input-confirmPass">Confirmar Contraseña</label>
                <b-input-group class="input-group-merge">
                  <b-form-input
                    id="input-confirmPass"
                    v-model="form.passwordConfirm"
                    autocomplete="new-password"
                    :type="passwordConfirmType"
                    placeholder="Confirma la contraseña"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordConfirmToggleIcon"
                      @click="togglePasswordConfirm"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-button
              type="submit"
              variant="primary"
            >
              Crear
            </b-button>
            <b-button
              style="margin-left: 5px;"
              type="reset"
              variant="secondary"
            >
              Limpiar
            </b-button>
          </b-form>
        </validation-observer>
      </template>

    </b-sidebar>
  </div>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import vSelect from 'vue-select'
import { required } from '@/utils/validations/validations'

// Cache para evitar requests repetidos
const validationCache = new Map()

// Agregar validación personalizada para employee_id único
extend('employee_id_unique', {
  validate: async value => {
    if (!value || value.trim() === '') return true // Si está vacío, no validar

    const trimmedValue = value.trim()

    // Verificar cache primero
    if (validationCache.has(trimmedValue)) {
      return validationCache.get(trimmedValue)
    }

    try {
      const response = await axios.get('system/users', {
        params: {
          query: trimmedValue,
          perPage: 1,
        },
      })

      // Si encuentra usuarios con ese employee_id, retorna false
      const users = response.data.data || []
      const hasEmployeeId = users.some(user => user.employee_id === trimmedValue)
      const result = !hasEmployeeId

      // Guardar en cache
      validationCache.set(trimmedValue, result)

      return result
    } catch (error) {
      return true // En caso de error, permitir continuar
    }
  },
  message: 'Este código de empleado ya está en uso',
})

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  model: {
    prop: 'userAddSidebarActive',
    event: 'update:user-add-sidebar-active',
  },
  props: {
    userAddSidebarActive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,

      form: {
        name: '',
        mail: '',
        employee_id: '',
        role: '',
        password: '',
        passwordConfirm: '',
        sales_target: '',
      },

      show: true,
      employeeIdError: '',
      employeeIdState: null,

      passwordType: 'password',
      passwordConfirmType: 'password',
      passwordToggleIcon: 'EyeIcon',
      passwordConfirmToggleIcon: 'EyeIcon',

      roles: [
        { value: 'admin', label: 'Administrador' },
        { value: 'vendor', label: 'Vendedor' },
      ],
    }
  },
  methods: {
    togglePassword() {
      this.passwordType = (this.passwordType === 'password') ? 'text' : 'password'
      this.passwordToggleIcon = (this.passwordType === 'password') ? 'EyeIcon' : 'EyeOffIcon'
    },
    togglePasswordConfirm() {
      this.passwordConfirmType = this.passwordConfirmType === 'password' ? 'text' : 'password'
      this.passwordConfirmToggleIcon = this.passwordConfirmType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },

    async validateEmployeeId() {
      if (!this.form.employee_id || this.form.employee_id.trim() === '') {
        this.employeeIdError = ''
        this.employeeIdState = null
        return
      }

      const trimmedValue = this.form.employee_id.trim()

      // Verificar cache primero
      if (validationCache.has(trimmedValue)) {
        const result = validationCache.get(trimmedValue)
        this.employeeIdError = result ? '' : 'Este código de empleado ya está en uso'
        this.employeeIdState = result
        return
      }

      try {
        const response = await axios.get('system/users', {
          params: {
            query: trimmedValue,
            perPage: 1,
          },
        })

        const users = response.data.data || []
        const hasEmployeeId = users.some(user => user.employee_id === trimmedValue)
        const result = !hasEmployeeId

        // Guardar en cache
        validationCache.set(trimmedValue, result)

        this.employeeIdError = result ? '' : 'Este código de empleado ya está en uso'
        this.employeeIdState = result
      } catch (error) {
        this.employeeIdError = ''
        this.employeeIdState = null
      }
    },

    onSubmit(event) {
      event.preventDefault()

      // Validar employee_id antes de enviar
      if (this.employeeIdError) {
        this.makeToast('danger', 'Error', 'Corrige los errores antes de continuar.')
        return
      }

      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          axios
            .post('system/register', this.form)
            .then(response => {
              this.makeToast('success', 'Usuario agregado', `Se ha creado el usuario ${response.data.data.name}.`)
              this.clearForm()
              this.$emit('update:user-add-sidebar-active', false)
              this.$emit('refreshData')
            })
            .catch(error => {
              this.showErrors(error)
            })
        } else {
          this.makeToast('danger', 'Error', 'No es posible crear el usuario.')
        }
      })
    },

    onReset(event) {
      event.preventDefault()
      this.clearForm()
    },

    clearForm() {
      // Reset our form values
      this.form.name = ''
      this.form.mail = ''
      this.form.employee_id = ''
      this.form.role = ''
      this.form.password = ''
      this.form.passwordConfirm = ''
      this.form.sales_target = ''

      // Reset employee_id validation
      this.employeeIdError = ''
      this.employeeIdState = null

      // Trick to reset/clear native browser form validation state
      this.show = false
      this.$nextTick(() => {
        this.show = true
      })
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },

    showErrors(error) {
      let errorMessage = 'Error al crear el usuario'

      if (error.response && error.response.data && error.response.data.errors) {
        const { errors } = error.response.data
        errorMessage = Object.values(errors).flat().join(', ')
      } else if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message
      }

      this.makeToast('danger', 'Error', errorMessage)
    },
  },
}
</script>
